import { useGeofencesQueryV1 } from "@/api/v1/geofence-api";
import { GeofencesTable } from "@/components/geofences/table/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "@tanstack/react-router";
import {
  Plus,
  MapPin,
  Shield,
  AlertTriangle,
  Activity, CheckCircle, Info,
} from "lucide-react";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { useFeatureAccess } from "@/hooks/use-subscription-limits";

export function GeofencesRoute() {
  const { data: geofences, isLoading, error } = useGeofencesQueryV1();
  const hasGeofences = geofences && geofences.length > 0;
  const activeGeofences = geofences?.length ?? 0;
  const totalGeofences = geofences?.length ?? 0;
  
  const { limit, isUnlimited: isUnlimitedGeofences } = useFeatureAccess(
    "geofences",
    totalGeofences
  );
  
  const hasReachedLimit = !isUnlimitedGeofences && limit > 0 && totalGeofences >= limit;

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Geofences", href: "/geofences" },
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Geofences</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {hasGeofences 
                ? "Manage your custom airspace boundaries and monitoring zones" 
                : "Create custom airspace boundaries to monitor drone activity and enforce restrictions"
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild disabled={hasReachedLimit}>
              <Link to="/geofences/create">
                <Plus className="h-4 w-4 mr-2" />
                Create Geofence
              </Link>
            </Button>
            {!hasGeofences && (
              <Badge variant="secondary" className="w-fit">
                <Shield className="h-3 w-3 mr-1" />
                No Boundaries
              </Badge>
            )}
            {hasReachedLimit && (
              <Badge variant="outline" className="w-fit">
                <Info className="h-3 w-3 mr-1" />
                {`${totalGeofences}/${limit} Limit Reached`}
              </Badge>
            )}
            {!hasReachedLimit && !isUnlimitedGeofences && limit > 0 && (
              <Badge variant="secondary" className="w-fit">
                <Info className="h-3 w-3 mr-1" />
                {totalGeofences}/{limit} Used
              </Badge>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        {hasGeofences && (
          <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Active Geofences</CardTitle>
                <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                {isLoading ? (
                  <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                ) : (
                  <div className="text-lg sm:text-2xl font-bold">{activeGeofences}</div>
                )}
                <p className="text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : `${totalGeofences} total boundaries`}
                </p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Monitoring Status</CardTitle>
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                <div className="text-lg sm:text-2xl font-bold text-green-600">Active</div>
                <p className="text-xs text-muted-foreground">
                  Real-time monitoring
                </p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Coverage Area</CardTitle>
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                <div className="text-lg sm:text-2xl font-bold">Custom</div>
                <p className="text-xs text-muted-foreground">
                  User-defined zones
                </p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Alert System</CardTitle>
                <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                <div className="text-lg sm:text-2xl font-bold text-green-600">Enabled</div>
                <p className="text-xs text-muted-foreground">
                  Violation detection
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-96" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-4 w-[100px]" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Failed to load geofences</p>
                <p>There was an error loading your geofence data. Please try refreshing the page.</p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        {!isLoading && !error && (
          <>
            {hasGeofences ? (
              <GeofencesTable data={geofences ?? []} />
            ) : (
              <Card className="border-2 border-dashed border-muted-foreground/25">
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 p-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Create Your First Geofence</CardTitle>
                  <CardDescription className="text-base">
                    Geofences are custom airspace boundaries that you can create to monitor drone activity 
                    and enforce restrictions. Define specific areas where you want to track or restrict drone operations.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <Badge variant="outline" className="text-sm">
                      <MapPin className="mr-2 h-4 w-4" />
                      Custom Boundaries
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-center">What you can do with geofences:</h4>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
                        <Shield className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium text-sm">Restricted Zones</p>
                          <p className="text-xs text-muted-foreground">Define no-fly areas</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
                        <Activity className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium text-sm">Activity Monitoring</p>
                          <p className="text-xs text-muted-foreground">Track drone movements</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
                        <AlertTriangle className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium text-sm">Violation Alerts</p>
                          <p className="text-xs text-muted-foreground">Real-time notifications</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Start by creating your first geofence to define custom airspace boundaries 
                      and begin monitoring drone activity in your area.
                    </p>
                    <Button asChild>
                      <Link to="/geofences/create">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Your First Geofence
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Limit Warning */}
            {hasReachedLimit && (
              <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">Plan Limit Reached</p>
                    <p>
                      You've reached the maximum of {limit} geofence{limit !== 1 ? 's' : ''} on your current plan. 
                      Delete an existing geofence to create a new one, or upgrade to Pro 
                      to create unlimited geofences.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Help Information */}
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Managing Geofences</p>
                  <p>
                    {hasGeofences 
                      ? "Create custom airspace boundaries to monitor drone activity and enforce restrictions. Use the map interface to draw precise zones and configure alert settings for each geofence."
                      : "Geofences allow you to define custom airspace boundaries for monitoring and restriction purposes. Draw polygons or circles on the map to create zones that will track drone activity and send alerts when violations occur."
                    }
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </>
        )}
      </div>
    </main>
  );
}
